import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LngLatBounds } from 'maplibre-gl';
import { message, Modal } from 'antd';
import styled from 'styled-components';
import { PiCommandBold, PiKeyboardBold, PiXBold } from 'react-icons/pi';
import MapUtils from '../utils/MapUtils';
import {
  isEditableElement,
  mapElementTypeToTerraDrawMode,
  parseBoundsTuple,
  resolveShortcutAction,
  ShortcutActionType,
} from '../utils/keyboardShortcutUtils';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { remote_updateProject } from '~/common/typescript/apis/ProjectAPIs';
import {
  allDisplayedDataBoundsAtom,
  mapContextMenuAtom,
  mapDataListAtom,
  showCommentPanelAtom,
  showDataTableAtom,
  showForestWatchPanelAtom,
  showGeocollectPanelAtom,
  showLegendPanelAtom,
  showMinimapAtom,
  showNavigatorPanelAtom,
  showSearchAddressPanelAtom,
  showUploadTaskPanelAtom,
} from '../stores/mapStore';
import { showMapViewAtom } from '~/layouts/components/Header/components/MapView/store';
import {
  headerCenter_currentState_Atom,
  HeaderCenter_State,
} from '~/layouts/components/Header/HeaderCenter/store';
import {
  elementType_Atom,
  showElementPanel_Atom,
} from '~/layouts/components/Header/HeaderCenter/Element/store';
import { ElementType } from '~/common/typescript/models/Element';
import { TerraDrawMode_Enum, TerraDrawUtil } from '~/components/MapDraw/TerraDrawUtil';

/**
 * KeyboardController
 * Manages map keyboard shortcuts (both direct shortcuts and `Ctrl + K` chorded commands)
 * and renders the interactive Command Chord HUD when `Ctrl + K` is active.
 */
const KeyboardController = () => {
  const [isCtrlKPressed, setIsCtrlKPressed] = useState(false);

  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const allDisplayedDataBounds = useAtomValue(allDisplayedDataBoundsAtom);
  const mapDataList = useAtomValue(mapDataListAtom);

  const setShowLegendPanel = useSetAtom(showLegendPanelAtom);
  const setShowDataTable = useSetAtom(showDataTableAtom);
  const setShowCommentPanel = useSetAtom(showCommentPanelAtom);
  const setShowGeocollectPanel = useSetAtom(showGeocollectPanelAtom);
  const setShowForestWatchPanel = useSetAtom(showForestWatchPanelAtom);
  const setShowUploadTaskPanel = useSetAtom(showUploadTaskPanelAtom);
  const setShowMinimap = useSetAtom(showMinimapAtom);
  const setShowMapViewPanel = useSetAtom(showMapViewAtom);
  const setShowSearchAddressPanel = useSetAtom(showSearchAddressPanelAtom);
  const [showNavigatorPanel, setShowNavigatorPanel] = useAtom(showNavigatorPanelAtom);
  const setMapContextMenu = useSetAtom(mapContextMenuAtom);

  const [headerCenterState, setHeaderCenterState] = useAtom(headerCenter_currentState_Atom);
  const setElementMode = useSetAtom(elementType_Atom);
  const setShowElementPanel = useSetAtom(showElementPanel_Atom);

  const activateDrawMode = useCallback(
    (targetType: ElementType) => {
      setHeaderCenterState(HeaderCenter_State.ELEMENT);
      setElementMode(targetType);
      setShowElementPanel(false);

      if (window.map) {
        if (!window.draw) {
          try {
            TerraDrawUtil.setup();
          } catch (err) {
            console.warn('TerraDraw setup warning:', err);
          }
        }
        if (window.draw && !window.draw.enabled) {
          try {
            window.draw.start();
          } catch (err) {
            console.warn('TerraDraw start warning:', err);
          }
        }
        if (window.draw && window.draw.enabled) {
          const drawMode = mapElementTypeToTerraDrawMode(targetType);
          try {
            window.draw.setMode(drawMode);
          } catch (err) {
            console.warn('TerraDraw setMode warning:', err);
          }
        }
      }
    },
    [setElementMode, setHeaderCenterState, setShowElementPanel],
  );

  const zoomToProjectDefaultView = useCallback(() => {
    const tuple = parseBoundsTuple(currentProject?.boundingBox);
    const bounds = tuple ? new LngLatBounds(tuple[0], tuple[1]) : undefined;
    MapUtils.zoomToDefaultView(bounds);
  }, [currentProject?.boundingBox]);

  const zoomToAllDisplayedData = useCallback(() => {
    const tuple = parseBoundsTuple(allDisplayedDataBounds);
    if (tuple) {
      MapUtils.zoomToDefaultView(new LngLatBounds(tuple[0], tuple[1]));
      return;
    }
    if (mapDataList && mapDataList.length > 0) {
      message.info(
        `Có ${mapDataList.length} dữ liệu bản đồ nhưng chưa có thông tin vùng hiển thị. Vui lòng kiểm tra dữ liệu bản đồ.`,
      );
    } else {
      message.info('Không có dữ liệu bản đồ để phóng tới.');
    }
  }, [allDisplayedDataBounds, mapDataList]);

  const saveDefaultProjectView = useCallback(() => {
    Modal.confirm({
      title: 'Lưu thu phóng mặc định',
      content: (
        <div>
          <p>Bạn có chắc chắn muốn lưu khung nhìn hiện tại làm thu phóng mặc định cho dự án này?</p>
          <p style={{ color: '#666', fontSize: '14px', marginTop: '12px' }}>
            <strong>Lưu ý:</strong> Hãy điều chỉnh camera để hiển thị khu vực dự án mong muốn trước khi lưu.
          </p>
        </div>
      ),
      okText: 'Lưu',
      cancelText: 'Hủy',
      onOk: async () => {
        if (window.map && currentProject?.id) {
          const bounds = window.map.getBounds();
          const newBoundingBox = JSON.stringify([
            [bounds.getWest(), bounds.getSouth()],
            [bounds.getEast(), bounds.getNorth()],
          ]);

          const updatedProject = await remote_updateProject(
            {
              boundingBox: newBoundingBox,
            },
            currentProject.id,
          );

          if (updatedProject?.data) {
            setCurrentProject(updatedProject.data);
            message.success('Đã lưu thu phóng mặc định!');
          } else {
            message.error('Lỗi khi lưu thu phóng mặc định.');
          }
        }
      },
    });
  }, [currentProject?.id, setCurrentProject]);

  const executeShortcutAction = useCallback(
    (action: ShortcutActionType) => {
      switch (action) {
        case 'TOGGLE_CHORD':
          break;
        case 'CANCEL_OR_ESCAPE':
          setMapContextMenu(null);
          setShowElementPanel(false);
          if (headerCenterState === HeaderCenter_State.ELEMENT) {
            setHeaderCenterState(HeaderCenter_State.NONE);
            if (window.draw && window.draw.enabled) {
              try {
                window.draw.setMode(TerraDrawMode_Enum.SELECT);
              } catch {
                // ignore
              }
            }
          }
          if (showNavigatorPanel) {
            setShowNavigatorPanel(false);
          }
          MapUtils.clearHighlight();
          break;
        case 'ZOOM_DEFAULT_VIEW':
          zoomToProjectDefaultView();
          break;
        case 'ZOOM_TO_DATA':
          zoomToAllDisplayedData();
          break;
        case 'SAVE_DEFAULT_VIEW':
          saveDefaultProjectView();
          break;
        case 'ZOOM_IN':
          MapUtils.zoomIn();
          break;
        case 'ZOOM_OUT':
          MapUtils.zoomOut();
          break;
        case 'DRAW_POINT':
          activateDrawMode(ElementType.POINT);
          break;
        case 'DRAW_LINESTRING':
          activateDrawMode(ElementType.LINESTRING);
          break;
        case 'DRAW_POLYGON':
          activateDrawMode(ElementType.POLYGON);
          break;
        case 'TOGGLE_LEGEND_PANEL':
          setShowLegendPanel((prev) => !prev);
          break;
        case 'TOGGLE_DATATABLE_PANEL':
          setShowDataTable((prev) => !prev);
          MapUtils.triggerResizeEvent();
          break;
        case 'TOGGLE_COMMENT_PANEL':
          setShowCommentPanel((prev) => !prev);
          break;
        case 'TOGGLE_GEOCOLLECT_PANEL':
          setShowGeocollectPanel((prev) => !prev);
          break;
        case 'TOGGLE_FOREST_WATCH_PANEL':
          setShowForestWatchPanel((prev) => !prev);
          break;
        case 'TOGGLE_UPLOAD_TASK_PANEL':
          setShowUploadTaskPanel((prev) => !prev);
          break;
        case 'TOGGLE_MINIMAP':
          setShowMinimap((prev) => !prev);
          break;
        case 'TOGGLE_MAP_VIEW_PANEL':
          setShowMapViewPanel((prev) => !prev);
          break;
        case 'TOGGLE_SEARCH_PANEL':
          setShowSearchAddressPanel((prev) => !prev);
          break;
        case 'TOGGLE_NAVIGATOR_PANEL':
          setShowNavigatorPanel((prev) => !prev);
          break;
        default:
          break;
      }
    },
    [
      activateDrawMode,
      headerCenterState,
      saveDefaultProjectView,
      setHeaderCenterState,
      setMapContextMenu,
      setShowCommentPanel,
      setShowDataTable,
      setShowElementPanel,
      setShowForestWatchPanel,
      setShowGeocollectPanel,
      setShowLegendPanel,
      setShowMapViewPanel,
      setShowMinimap,
      setShowNavigatorPanel,
      setShowSearchAddressPanel,
      setShowUploadTaskPanel,
      showNavigatorPanel,
      zoomToAllDisplayedData,
      zoomToProjectDefaultView,
    ],
  );

  // Keep latest callback ref to avoid re-binding window keydown listener on every atom change
  const executeRef = useRef(executeShortcutAction);
  executeRef.current = executeShortcutAction;

  const isChordRef = useRef(isCtrlKPressed);
  isChordRef.current = isCtrlKPressed;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const editingInput = isEditableElement(event.target) || isEditableElement(document.activeElement);

      // Avoid firing single-key drawing/zoom shortcuts when an Ant Design Modal is open
      const hasOpenModal = Boolean(
        document.querySelector('.ant-modal-wrap:not([style*="display: none"])'),
      );
      if (hasOpenModal && !event.ctrlKey && !event.metaKey && event.key !== 'Escape') {
        return;
      }

      const resolution = resolveShortcutAction(event, isChordRef.current, editingInput);

      if (resolution.preventDefault) {
        event.preventDefault();
      }

      if (resolution.nextChordState !== isChordRef.current) {
        isChordRef.current = resolution.nextChordState;
        setIsCtrlKPressed(resolution.nextChordState);
      }

      if (resolution.action && resolution.action !== 'TOGGLE_CHORD') {
        executeRef.current(resolution.action);
      }
    };

    const handleTriggerShortcut = (event: Event) => {
      const customEvent = event as CustomEvent<{ action: ShortcutActionType; closeCheatsheet?: boolean }>;
      const action = customEvent?.detail?.action;
      if (!action) return;
      if (customEvent?.detail?.closeCheatsheet) {
        setShowNavigatorPanel(false);
      }
      if (action === 'TOGGLE_CHORD') {
        isChordRef.current = true;
        setIsCtrlKPressed(true);
        return;
      }
      executeRef.current(action);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('geocloud:trigger-shortcut', handleTriggerShortcut as EventListener);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('geocloud:trigger-shortcut', handleTriggerShortcut as EventListener);
    };
  }, [setShowNavigatorPanel]);

  const triggerHudAction = (action: ShortcutActionType) => {
    setIsCtrlKPressed(false);
    isChordRef.current = false;
    executeShortcutAction(action);
  };

  if (!isCtrlKPressed) {
    return null;
  }

  return (
    <UI_Wrapper data-testid="keyboard-chord-hud">
      <UI_HeaderRow>
        <UI_TitleBadge>
          <PiCommandBold size={15} />
          <span>Ctrl + K (đang lắng nghe)</span>
        </UI_TitleBadge>
        <UI_CloseBtn
          data-testid="keyboard-chord-close"
          onClick={() => {
            isChordRef.current = false;
            setIsCtrlKPressed(false);
          }}
          title="Đóng (Esc)"
        >
          <span>Esc</span>
          <PiXBold size={12} />
        </UI_CloseBtn>
      </UI_HeaderRow>

      <UI_ShortcutGrid>
        <UI_ShortcutPill onClick={() => triggerHudAction('ZOOM_DEFAULT_VIEW')} data-testid="chord-action-default-zoom">
          <kbd>0</kbd>
          <span>Mặc định</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('ZOOM_TO_DATA')} data-testid="chord-action-zoom-data">
          <kbd>F</kbd>
          <span>Phóng tới dữ liệu</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('SAVE_DEFAULT_VIEW')} data-testid="chord-action-save-view">
          <kbd>S</kbd>
          <span>Lưu góc nhìn</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('ZOOM_IN')} data-testid="chord-action-zoom-in">
          <kbd>+</kbd>
          <span>Phóng to</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('ZOOM_OUT')} data-testid="chord-action-zoom-out">
          <kbd>-</kbd>
          <span>Thu nhỏ</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('DRAW_POINT')} data-testid="chord-action-draw-point">
          <kbd>P</kbd>
          <span>Vẽ Điểm</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('DRAW_LINESTRING')} data-testid="chord-action-draw-line">
          <kbd>L</kbd>
          <span>Vẽ Đường</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('DRAW_POLYGON')} data-testid="chord-action-draw-polygon">
          <kbd>R</kbd>
          <span>Vẽ Vùng</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('TOGGLE_LEGEND_PANEL')} data-testid="chord-action-legend">
          <kbd>1</kbd>
          <span>Chú giải</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('TOGGLE_DATATABLE_PANEL')} data-testid="chord-action-datatable">
          <kbd>2</kbd>
          <span>Bảng dữ liệu</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('TOGGLE_COMMENT_PANEL')} data-testid="chord-action-comment">
          <kbd>3</kbd>
          <span>Ghi chú</span>
        </UI_ShortcutPill>
        <UI_ShortcutPill onClick={() => triggerHudAction('TOGGLE_NAVIGATOR_PANEL')} data-testid="chord-action-help">
          <PiKeyboardBold size={13} />
          <kbd>?</kbd>
          <span>Tất cả phím tắt</span>
        </UI_ShortcutPill>
      </UI_ShortcutGrid>
    </UI_Wrapper>
  );
};

export default memo(KeyboardController);

const UI_Wrapper = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 16px;
  min-width: 480px;
  max-width: 680px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.92);
  color: #f8fafc;
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(255, 255, 255, 0.12);
  z-index: 2500;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UI_HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const UI_TitleBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #60a5fa;
`;

const UI_CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
  font-size: 11px;
  font-family: monospace;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    color: #ffffff;
  }
`;

const UI_ShortcutGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const UI_ShortcutPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(30, 41, 59, 0.85);
  color: #e2e8f0;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  kbd {
    display: inline-block;
    padding: 1px 5px;
    border-radius: 4px;
    background: rgba(59, 130, 246, 0.25);
    border: 1px solid rgba(96, 165, 250, 0.4);
    color: #93c5fd;
    font-family: monospace;
    font-size: 11px;
    font-weight: 600;
  }

  &:hover {
    background: rgba(59, 130, 246, 0.25);
    border-color: rgba(96, 165, 250, 0.5);
    color: #ffffff;
  }
`;
